import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, ImageIcon } from "lucide-react";
import { Overline, Reveal, ease } from "./Primitives";
import { useLang, useT } from "@/lib/i18n";
import { works } from "@/lib/works";

const Frame = ({ src, label, show }) => (
  <motion.div
    className="absolute inset-0"
    initial={false}
    animate={{ opacity: show ? 1 : 0, scale: show ? 1 : 1.04 }}
    transition={{ duration: 0.7, ease }}
    style={{ pointerEvents: show ? "auto" : "none" }}
  >
    {src ? (
      <img src={src} alt={label} loading="lazy" className="w-full h-full object-cover object-top" />
    ) : (
      <div className="w-full h-full grid place-items-center border border-dashed border-[var(--gold)]/40 rounded-[20px] bg-[#0c0a09]/60 p-6 text-center">
        <div>
          <ImageIcon className="mx-auto text-[var(--gold)]" size={28} />
          <p className="mt-3 font-mono text-xs uppercase tracking-[0.2em] text-stone-300">[{label}]</p>
        </div>
      </div>
    )}
  </motion.div>
);

const WorkCard = ({ w, i, t }) => {
  const [after, setAfter] = useState(true);
  const { lang } = useLang();
  const n = String(i + 1).padStart(2, "0");
  return (
    <Reveal delay={(i % 2) * 0.12} className="h-full">
      <article data-testid={`work-card-${w.id}`} className="bento-cell h-full flex flex-col rounded-[28px] border border-[var(--line)] bg-[var(--card)] p-4 md:p-5">
        <div className="relative aspect-[4/5] overflow-hidden rounded-[20px] bg-[#1c1917]">
          <Frame src={w.before} label={`${t.before} — ${w.name}`} show={!after} />
          <Frame src={w.after} label={`${t.after} — ${w.name}`} show={after} />
          <span className="absolute top-4 left-4 font-mono text-[11px] uppercase tracking-[0.2em] bg-[#0c0a09]/80 backdrop-blur px-3 py-1.5 rounded-full text-[var(--gold)]" data-testid={`work-state-${w.id}`}>
            {w.real ? t.live : after ? t.after : t.before}
          </span>
        </div>
        <div className="mt-5 flex flex-wrap items-center justify-between gap-4 px-1">
          <div>
            <p className="font-mono text-xs text-[var(--gold)]">{n} · {t.types[w.type]}</p>
            <h3 className="font-display text-2xl leading-tight" data-testid={`work-name-${w.id}`}>{w.name}</h3>
          </div>
          {w.real ? (
            <a href={w.url} target="_blank" rel="noopener noreferrer" data-testid={`work-${w.id}-visit-link`}
              className="inline-flex items-center gap-1.5 rounded-full border border-[var(--line)] px-4 py-2 text-sm text-stone-200 hover:border-[var(--gold)] hover:text-[var(--gold)] transition-colors">
              {t.visit} <ArrowUpRight size={16} />
            </a>
          ) : (
          <div className="flex rounded-full border border-[var(--line)] p-1 text-sm" data-testid={`work-toggle-${w.id}`}>
            {[false, true].map((v) => (
              <button key={String(v)} data-testid={`work-${w.id}-${v ? "after" : "before"}-btn`} onClick={() => setAfter(v)}
                className={`px-4 py-1.5 rounded-full transition-colors duration-300 ${after === v ? "bg-[var(--gold)] text-[#0c0a09]" : "text-stone-300 hover:text-[var(--fg)]"}`}>
                {v ? t.after : t.before}
              </button>
            ))}
          </div>
          )}
        </div>
        {w.quote ? (
        <figure className="mt-5 mx-1 pt-5 border-t border-[var(--line)] flex-1" data-testid={`work-review-${w.id}`}>
          <div className="text-[var(--gold)] text-sm tracking-[0.2em]" aria-label="5/5">★★★★★</div>
          <blockquote className="mt-2 font-display italic text-lg leading-snug text-stone-200">“{w.quote?.[lang]}”</blockquote>
          <figcaption className="mt-3 font-mono text-[11px] uppercase tracking-[0.2em] text-stone-400">{w.author?.[lang]}</figcaption>
        </figure>
        ) : (
          <p className="mt-5 mx-1 pt-5 border-t border-[var(--line)] flex-1 text-sm text-stone-300 leading-relaxed" data-testid={`work-desc-${w.id}`}>{t.realDesc?.[w.id]}</p>
        )}
      </article>
    </Reveal>
  );
};

export const Works = () => {
  const t = useT().works;
  return (
    <section id="portfolio" className="py-24 md:py-36">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10">
        <Reveal>
          <Overline>{t.over}</Overline>
          <h2 className="mt-6 font-display font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight max-w-3xl">{t.title}</h2>
          <p className="mt-6 max-w-xl text-stone-300">{t.sub}</p>
        </Reveal>
        <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {works.map((w, i) => <WorkCard key={w.id} w={w} i={i} t={t} />)}
        </div>
      </div>
    </section>
  );
};
